<template>
  <div>
    <!-- Hero Section with Integrated Action Cards -->
    <section class="reveal-section" data-reveal>
      <HomePageHeroSection />
    </section>

    <!-- Category Navigation -->
    <section class="reveal-section" data-reveal>
      <HomePageCategoryNavigation />
    </section>

    <!-- Featured Machines Stock List -->
    <section class="reveal-section" data-reveal>
      <HomePageFeaturedMachines />
    </section>

    <!-- Removed Logistics & Services section as requested -->

    <!-- Trusted Brands -->
    <section class="reveal-section" data-reveal>
      <HomePageBrandsSection />
    </section>

    <!-- Newsletter Signup (Keep existing) -->
    <section class="reveal-section" data-reveal>
      <HomePageNewsletterSignup />
    </section>
  </div>
</template>

<script setup>
import { ref, onMounted } from "vue";
import HomePageHeroSection from "~/components/HomePage/HeroSectionClean.vue";
import HomePageCategoryNavigation from "~/components/HomePage/CategoryNavigation.vue";
import HomePageFeaturedMachines from "~/components/HomePage/FeaturedMachinesClean.vue";
import HomePageLogisticsServices from "~/components/HomePage/LogisticsServices.vue";
import HomePageBrandsSection from "~/components/HomePage/BrandsSection.vue";
import HomePageNewsletterSignup from "~/components/HomePage/NewsletterSignup.vue";

const { t } = useI18n();

useHead({
  title: t('meta.homeTitle'),
  meta: [
    {
      name: "description",
      content: t('meta.homeDescription')
    },
    {
      name: "keywords",
      content:
        "industrial printing equipment, offset printing presses, Heidelberg presses, Man Roland equipment, digital printing systems, cutting machines, binding equipment, finishing machinery, commercial printing, packaging equipment, print service providers, graphic arts machinery",
    },
    {
      property: "og:title",
      content: t('meta.homeTitle')
    },
    {
      property: "og:description",
      content: t('meta.homeDescription')
    },
    { property: "og:type", content: "website" },
    { property: "og:url", content: useRuntimeConfig().public.appUrl || "https://technomac-snc.com" },
    { name: "twitter:card", content: "summary_large_image" },
    {
      name: "twitter:title",
      content: t('meta.homeTitle')
    },
    {
      name: "twitter:description",
      content: t('meta.homeDescription')
    },
  ],
});

onMounted(() => {
  const eventName = 'home_page_view';
  useTrackEvent(eventName, {
    page_location: window.location.href,
  });
  const gtagConfig = useRuntimeConfig().public.gtag;
  if (import.meta.dev && gtagConfig.enabled && gtagConfig.id) {
    console.info('[gtag] event triggered', eventName);
  }

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('in-view');
          // optionally unobserve so animation runs once
          observer.unobserve(entry.target);
        }
      });
    },
    { threshold: 0.15 }
  );

  document.querySelectorAll('[data-reveal]').forEach((el) => {
    // set initial state
    el.classList.add('reveal');
    observer.observe(el);
  });
});
</script>

<style scoped>
/* Global styles for the homepage */
.container-custom {
  @apply max-w-7xl mx-auto px-4 sm:px-6 lg:px-8;
}

/* Smooth scrolling */
html {
  scroll-behavior: smooth;
}

/* Ensure normal page scrolling and avoid scroll-lock from transitions */
html, body {
  overscroll-behavior-y: auto;
  -webkit-overflow-scrolling: touch;
  overflow-y: auto;
}

/* Reveal-on-scroll sections */
.reveal {
  opacity: 0;
  transform: translateY(12px);
  transition: opacity 520ms cubic-bezier(0.22, 1, 0.36, 1), transform 520ms cubic-bezier(0.22, 1, 0.36, 1);
  /* prevent scroll anchoring / layout jumps when revealing */
  overflow-anchor: none;
}
.reveal.in-view {
  opacity: 1;
  transform: translateY(0);
}

/* Prevent reveal transforms from creating backface artifacts */
.reveal, .reveal-section {
  backface-visibility: hidden;
  -webkit-backface-visibility: hidden;
  overflow: visible;
}

/* Custom animations */
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.animate-fade-in-up {
  animation: fadeInUp 0.6s ease-out;
}

/* Enhanced focus states */
.focus\:ring-2:focus {
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.5);
}

/* Custom gradients */
.bg-gradient-to-r {
  background: linear-gradient(to right, var(--tw-gradient-stops));
}

.bg-gradient-to-br {
  background: linear-gradient(to bottom right, var(--tw-gradient-stops));
}

/* Enhanced shadows */
.shadow-lg {
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1),
    0 4px 6px -2px rgba(0, 0, 0, 0.05);
}

.shadow-xl {
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1),
    0 10px 10px -5px rgba(0, 0, 0, 0.04);
}

/* Smooth transitions */
.transition-all {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

/* Hover effects */
.hover\:scale-105:hover {
  transform: scale(1.05);
}

/* Responsive text */
@media (max-width: 640px) {
  .text-4xl {
    font-size: 2rem;
  }

  .text-xl {
    font-size: 1.125rem;
  }
}

@media (min-width: 1024px) {
  .text-4xl {
    font-size: 2.5rem;
  }
}
</style>
